<template>
    <b-container>
        <b-row align-h="center">
            <b-col sm="6" md="4">
                <b-form novalidate>
                    <div
                        class="card-header text-secondary-light header-text text-center"
                        style="border-bottom: none; font-weight: 300;">
                        {{ $t('login.title') }}
                    </div><br>
                    <b-card header-bg-variant="primary"
                            header-text-variant="secondary-light"
                            :header="$t('login.enaioUsersTitle')"
                            class="text-center">
                        <b-input-group label-for="email">    
                            <b-input-group-prepend>
                                <span class="input-group-text">
                                    <icon name="user"></icon>
                                </span>
                            </b-input-group-prepend>
                            <b-form-input name="username" id="email"
                                        v-validate="'required'"
                                        type="email"
                                        v-model="form.email"
                                        required
                                        :placeholder="$t('login.username')"
                                        @keyup.enter.native="dologin"
                                        :disabled="isLoading"
                                        :state="!veeErrors.has('username')">
                            </b-form-input>
                        </b-input-group>
                        <span v-show="veeErrors.has('username')" style="color: red;" class="small">{{ $t('login.validation.username') }}</span>
                        <b-input-group label-for="password" class="mt-3">
                            <b-input-group-prepend>
                                <span class="input-group-text">
                                    <icon name="lock"></icon>
                                </span>
                            </b-input-group-prepend>
                            <b-form-input name="password" id="password"
                                        v-validate="'required'"
                                        type="password"
                                        v-model="form.password"
                                        required
                                        :placeholder="$t('login.password')"
                                        @keyup.enter.native="dologin"
                                        :disabled="isLoading"
                                        :state="!veeErrors.has('password')">
                            </b-form-input>
                        </b-input-group>
                        <span v-show="veeErrors.has('password')" style="color: red;" class="small">{{ $t('login.validation.password') }}</span>
                        <b-button variant="primary" class="mt-3"
                                  @click="dologin"
                                  :disabled="isLoading">
                            {{ $t("login.login") }}
                        </b-button>
                    </b-card>
                </b-form>

                <hr class="hr-text" :data-content="$t('general.or').toUpperCase()" v-if="socialLoginEnabled && (enableGoogle || enableLinkedin || enableMicrosoft || enableKeycloak)">
                <b-card
                        header-bg-variant="primary"
                        header-text-variant="secondary-light"
                        :header="$t('login.externalUsersTitle')"
                        class="text-center"
                        align="center"
                        v-if="socialLoginEnabled && (enableGoogle || enableLinkedin || enableMicrosoft || enableKeycloak)">
                    <div v-if="enableGoogle">
                        <b-button :href="`${baseUrl}/epr/login/google`"
                                  class="btn btn-primary"
                                  :disabled="isLoading"
                                  style="width: 100%">
                            <icon name="google"></icon>&nbsp;
                            <span>{{ $t('login.socialLogins.google') }}</span>
                        </b-button>
                    </div><br v-if="enableGoogle">

                    <div v-if="enableLinkedin">
                        <b-button :href="`${baseUrl}/epr/login/linkedin`"
                                  class="btn btn-primary"
                                  :disabled="isLoading"
                                  style="width: 100%">
                            <icon name="linkedin"></icon>&nbsp;
                            <span>{{ $t('login.socialLogins.linkedin') }}</span>
                        </b-button>
                    </div><br v-if="enableLinkedin">

                    <div v-if="enableMicrosoft">
                        <b-button :href="`${baseUrl}/epr/login/microsoft`"
                                  class="btn btn-primary"
                                  :disabled="isLoading"
                                  style="width: 100%">
                            <icon name="windows"></icon>&nbsp;
                            <span>{{ $t('login.socialLogins.microsoft') }}</span>
                        </b-button>
                    </div><br v-if="enableMicrosoft">

                    <div v-if="enableKeycloak">
                        <b-button :href="`${baseUrl}/epr/login/keycloak`"
                                  class="btn btn-primary company-login"
                                  :disabled="isLoading"
                                  style="width: 100%">
                            <icon name="sign-in"></icon>&nbsp;
                            <span>{{ $t('login.socialLogins.keycloak', {companyName}) }}</span>
                        </b-button>
                    </div>
                </b-card>

                <hr>
                <b-form-group class="text-center">
                    <b-button-group>
                        <b-button variant="outline-primary" 
                                  @click="setLanguage('de')" 
                                  :class="{active: currentLanguage === 'de'}"
                                  :disabled="isLoading">
                            <span class="flag-icon flag-icon-de"></span>&nbsp;
                            Deutsch
                        </b-button>
                        <b-button variant="outline-primary" 
                                  @click="setLanguage('en')" 
                                  :class="{active: currentLanguage === 'en'}"
                                  :disabled="isLoading">
                            <span class="flag-icon flag-icon-gb"></span>&nbsp;
                            English
                        </b-button>
                    </b-button-group>
                </b-form-group>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'login',

    data () {
        return {
            form: {
                email: '',
                password: ''
            },

            socialLoginEnabled: false,
            enableGoogle: false,
            enableLinkedin: false,
            enableMicrosoft: false,
            enableKeycloak: false,
            companyName: '',
            baseUrl: ''
        }
    },

    created() {
        this.$store.dispatch('system/loadGlobalConfiguration')
            .then(response => {
                this.socialLoginEnabled = response.enablesocial;
                this.enableGoogle = response.authenable_google;
                this.enableLinkedin = response.authenable_linkedin;
                this.enableMicrosoft = response.authenable_microsoft;
                this.enableKeycloak = response.authenable_keycloak;
                this.companyName = response.keycloak_title;
                this.baseUrl = response.baseUrl;
                return response;
            })
            .then(response => {
                this.setLanguage(response.language);
            });
    },

    computed: {
        currentLanguage() {
            return this.$store.getters['userData/currentLanguage'];
        },
        ...mapState('system', ['isLoading'])
    },

    methods: {
        setLanguage(lang) {
            this.$store.dispatch('userData/setLanguage', lang);
        },

        dologin() {
            this.$validator.validateAll().then(result => {
                if(result) {
                    this.$store.dispatch('system/setLoadingStatus', true);

                    let creds = {
                        name: this.form.email,
                        pwd: this.form.password,
                    };

                    this.$store.dispatch('system/doFormLogin', { creds: creds })
                        .then((res) => {
                            if(res.status === 200) {
                                console.log('Logged in as: ', creds.name);
                                localStorage.setItem('login', localStorage.getItem('login')+1);
                                this.$router.push({name: 'dashboard'});
                            }
                        })
                        .catch(error => {
                            console.log('Login error: ', error);
                            this.$store.dispatch('system/setLoadingStatus', false);
                        });
                    }
            })
        }
    },
    beforeRouteLeave (to, from, next) {
        next();
        this.$store.dispatch('system/setLoadingStatus', false);
    }
}
</script>

<style scoped>
/* fixes vue-awesome icon (svg) positioning */

.header-text{
    font-size: 1.6rem;
    padding: 1rem;

}
.header-text > div:first-child{
    background-color: #f1f1f1 !important;
    box-shadow: inset 0 0 1rem rgba(0, 0, 0, 0.05);
    border-bottom: none;
}

.fa-icon {
    vertical-align: middle;
}

.input-group-text{
    background-color: #f1f1f1;
    color: #777;
}

.hr-text {
    line-height: 1em;
    position: relative;
    outline: 0;
    border: 0;
    color: black;
    text-align: center;
    height: 1.5em;
    opacity: .5;
}

.hr-text:before {
    content: '';
    background: linear-gradient(to right, transparent, #818078, transparent);
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 1px;
}

.hr-text:after {
    content: attr(data-content);
    position: relative;
    display: inline-block;
    color: black;
    padding: 0 .5em;
    line-height: 1.5em;
    color: #818078;
    background-color: #fcfcfa;
}

.small {
    text-align: left;
    display: block;
    font-size: 50%;
}

.company-login {
    white-space: unset;
}
</style>
